export const parseNDJSON = (text) => String(text || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .flatMap((line) => {
        try {
            return [JSON.parse(line)];
        } catch {
            return [];
        }
    });

const severityNames = ['emergency', 'alert', 'critical', 'error', 'warning', 'notice', 'info', 'debug'];

const firstValue = (...values) => values.find((value) => value != null && String(value).trim() !== '');

const isUnknown = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    return normalized === '' || normalized === 'unknown';
};

const parseSyslogMessage = (rawMessage = '') => {
    const withoutPriority = String(rawMessage || '').trim().replace(/^<\d+>/, '');
    const fullMatch = /^(?<month>[A-Z][a-z]{2})\s+(?<day>\d{1,2})\s+(?<clock>\d\d:\d\d:\d\d)\s+(?<host>\S+)\s+(?<service>[^:[\s]+)(?:\[\d+\])?:\s?(?<message>.*)$/.exec(withoutPriority);
    if (fullMatch?.groups) {
        return fullMatch.groups;
    }

    const serviceMatch = /^(?<service>[\w./-]+)(?:\[\d+\])?:\s+(?<message>.+)$/.exec(withoutPriority);
    if (serviceMatch?.groups) {
        return serviceMatch.groups;
    }

    return {};
};

const systemdUnitPattern = /^([\w.@-]+?)\.(service|socket|timer|target|mount|path|scope|slice|device|automount|swap):\s?(.*)$/;

const parseSystemdUnitMessage = (message = '') => {
    const match = systemdUnitPattern.exec(String(message || '').trim());
    if (!match) return {};
    return {
        unit: `${match[1]}.${match[2]}`,
        service: match[1],
        message: match[3],
    };
};

const levelFromPriority = (value) => {
    const raw = String(value || '').trim();
    const match = /^<(\d+)>/.exec(raw);
    const priority = match ? Number(match[1]) : (/^\d+$/.test(raw) ? Number(raw) : NaN);
    if (!Number.isFinite(priority)) return '';
    const severity = priority % 8;
    return severityNames[severity] || '';
};

const inferLevel = (message = '') => {
    const priorityLevel = levelFromPriority(message);
    if (priorityLevel) return priorityLevel;

    const lower = String(message || '').toLowerCase();
    if (/(emerg|alert|critical|crit|fatal|panic)/.test(lower)) return 'critical';
    if (/(error|\berr\b|failed|failure|denied|invalid|timeout|timed out|refused|unreachable)/.test(lower)) return 'error';
    if (/(warning|warn)/.test(lower)) return 'warning';
    if (/notice/.test(lower)) return 'notice';
    if (/debug/.test(lower)) return 'debug';
    return 'info';
};

export const normalizeLogEntry = (entry = {}) => {
    const rawMessage = firstValue(entry._msg, entry.message, entry.body, entry.MESSAGE, JSON.stringify(entry));
    const parsed = parseSyslogMessage(rawMessage);
    const directSystemdUnit = parseSystemdUnitMessage(rawMessage);
    const parsedMessage = parsed.message || rawMessage;
    const systemdUnit = directSystemdUnit.service ? directSystemdUnit : parseSystemdUnitMessage(parsedMessage);
    const rawLevel = firstValue(entry.level, entry.severity_text, entry.severity, entry.PRIORITY);
    const rawService = firstValue(entry.service, entry['service.name'], entry.SYSLOG_IDENTIFIER, entry._SYSTEMD_UNIT);
    const normalizedRawService = String(rawService || '').trim().toLowerCase();
    const shouldInferService = isUnknown(rawService) || ['router', 'systemd'].includes(normalizedRawService);
    const service = systemdUnit.service && (shouldInferService || parsed.service === 'systemd')
        ? systemdUnit.service
        : shouldInferService && parsed.service ? parsed.service : (rawService || 'unknown');

    return {
        raw: entry,
        time: entry._time || entry.timestamp || entry['@timestamp'] || '',
        level: levelFromPriority(rawLevel) || (isUnknown(rawLevel) ? inferLevel(rawMessage) : rawLevel),
        service,
        host: firstValue(entry.host, entry.hostname, entry['host.name'], entry._HOSTNAME, parsed.host) || 'unknown',
        message: systemdUnit.message || parsedMessage,
    };
};

const parseJSONDocuments = (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed) return [];
    try {
        const value = JSON.parse(trimmed);
        return Array.isArray(value) ? value : [value];
    } catch {
        return parseNDJSON(trimmed);
    }
};

export const parseHits = (text) => {
    const documents = parseJSONDocuments(text);
    const totalsByTime = new Map();
    documents.forEach((document) => {
        const series = Array.isArray(document.hits) && document.hits.some((item) => item?.timestamps)
            ? document.hits
            : [document];
        series.forEach((item) => {
            const timestamps = item.timestamps || item.times || [];
            const values = item.values || item.hits || [];
            if (Array.isArray(timestamps) && Array.isArray(values)) {
                timestamps.forEach((timestamp, index) => {
                    const value = Number(values[index] || 0);
                    totalsByTime.set(timestamp, (totalsByTime.get(timestamp) || 0) + value);
                });
            } else if (item._time || item.time) {
                const timestamp = item._time || item.time;
                totalsByTime.set(timestamp, (totalsByTime.get(timestamp) || 0) + Number(item.hits || item.count || 0));
            }
        });
    });
    return [...totalsByTime.entries()]
        .map(([time, hits]) => ({ time, hits }))
        .sort((a, b) => new Date(a.time) - new Date(b.time));
};

export const parseFacetOptions = (text) => {
    const documents = parseJSONDocuments(text);
    const result = { levels: [], services: [], hosts: [] };
    const addValues = (target, values) => {
        (values || []).forEach((item) => {
            const value = typeof item === 'string' ? item : (item?.value ?? item?.field_value);
            if (value != null && value !== '' && !target.includes(String(value))) target.push(String(value));
        });
    };

    documents.forEach((document) => {
        const facetDocuments = Array.isArray(document.facets) ? document.facets : [document];
        facetDocuments.forEach((facet) => {
            const field = facet.field || facet.field_name || facet.name;
            const values = facet.values || facet.field_values;
            if (['level', 'severity', 'severity_text'].includes(field)) addValues(result.levels, values);
            if (['service', 'service.name', 'SYSLOG_IDENTIFIER', '_SYSTEMD_UNIT'].includes(field)) addValues(result.services, values);
            if (['host', 'hostname', 'host.name', '_HOSTNAME'].includes(field)) addValues(result.hosts, values);
        });
        if (document.fields && typeof document.fields === 'object') {
            addValues(result.levels, document.fields.level || document.fields.severity_text);
            addValues(result.services, document.fields.service || document.fields['service.name']);
            addValues(result.hosts, document.fields.host || document.fields.hostname || document.fields['host.name'] || document.fields._HOSTNAME);
        }
    });
    result.levels.sort();
    result.services.sort();
    result.hosts.sort();
    return result;
};

export const rangeBounds = (range) => {
    const end = new Date();
    const match = /^(\d+)(m|h|d)$/.exec(range || '24h');
    const amount = Number(match?.[1] || 24);
    const unit = match?.[2] || 'h';
    const milliseconds = amount * (unit === 'm' ? 60000 : unit === 'd' ? 86400000 : 3600000);
    return {
        start: new Date(end.getTime() - milliseconds).toISOString(),
        end: end.toISOString(),
    };
};
